import React, { useState, useMemo } from 'react';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import { useNotifications } from '@/contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { formatDistanceToNow } from 'date-fns';

const formatTime = (timeValue) => {
    if (!timeValue) return 'Just now';
    try {
        let date = timeValue;
        if (typeof timeValue?.toDate === 'function') {
            date = timeValue.toDate();
        } else if (typeof timeValue === 'string') {
            date = new Date(timeValue);
        }
        
        if (isNaN(date.getTime())) return 'Just now';
        
        const distance = formatDistanceToNow(date, { addSuffix: true });
        return distance.replace('about ', '');
    } catch {
        return 'Just now';
    }
};

// Strips raw emoji prefixes if present in legacy payloads
const cleanTitle = (rawTitle) => {
    if (!rawTitle) return '';
    return rawTitle.replace(/^[\p{Emoji}\u200d\s]+/u, '').trim();
};

const APP_CONFIG = {
    admin: {
        label: 'Superadmin',
        icon: 'lock_reset',
        color: '#e11d48',
        bg: 'rgba(225, 29, 72, 0.1)',
        border: 'rgba(225, 29, 72, 0.2)'
    },
    automation: {
        label: 'Apps Script',
        icon: 'bolt',
        color: '#f59e0b',
        bg: 'rgba(245, 158, 11, 0.1)',
        border: 'rgba(245, 158, 11, 0.2)'
    },
    mentions: {
        label: 'Mention',
        icon: 'alternate_email',
        color: '#2563eb',
        bg: 'rgba(37, 99, 235, 0.1)',
        border: 'rgba(37, 99, 235, 0.2)'
    },
    risk: {
        label: 'Risk Alert',
        icon: 'warning',
        color: '#ef4444',
        bg: 'rgba(239, 68, 68, 0.1)',
        border: 'rgba(239, 68, 68, 0.2)'
    },
    data: {
        label: 'Data Export',
        icon: 'swap_vert',
        color: '#06b6d4',
        bg: 'rgba(6, 182, 212, 0.1)',
        border: 'rgba(6, 182, 212, 0.2)'
    },
    collab: {
        label: 'Transfers',
        icon: 'send',
        color: '#10b981',
        bg: 'rgba(16, 185, 129, 0.1)',
        border: 'rgba(16, 185, 129, 0.2)'
    },
    qms: {
        label: 'QMS',
        icon: 'fact_check',
        color: '#10b981',
        bg: 'rgba(16, 185, 129, 0.1)',
        border: 'rgba(16, 185, 129, 0.2)'
    },
    sales: {
        label: 'Sales & Deals',
        icon: 'badge',
        color: '#3b82f6',
        bg: 'rgba(59, 130, 246, 0.1)',
        border: 'rgba(59, 130, 246, 0.2)'
    },
    system: {
        label: 'System',
        icon: 'notifications',
        color: '#64748b',
        bg: 'rgba(100, 116, 139, 0.1)',
        border: 'rgba(100, 116, 139, 0.2)'
    }
};

const listContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.015,
            delayChildren: 0.01
        }
    }
};

const listItemVariants = {
    hidden: { opacity: 0, y: 6 },
    visible: { 
        opacity: 1, 
        y: 0, 
        transition: { duration: 0.15, ease: 'easeOut' } 
    },
    exit: { 
        opacity: 0, 
        transition: { duration: 0.1 } 
    }
};

/**
 * Vector-accurate animated Material Symbols Rounded notification_sound icon.
 * Specifically animates the two acoustic vibration arcs emitting from the bell.
 */
function NotificationSoundVibrationIcon({ isSoundOn = true, size = 16, color = "currentColor", className = "" }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 -960 960 960"
            width={size}
            height={size}
            className={`shrink-0 overflow-visible ${className}`}
            style={{ display: 'inline-block', verticalAlign: 'middle' }}
        >
            {/* 1. Clapper */}
            <path
                d="M480-80q-33 0-56.5-23.5T400-160h160q0 33-23.5 56.5T480-80Z"
                fill={color}
                opacity={isSoundOn ? 1 : 0.4}
            />

            {/* 2. Bell Body */}
            <path
                d="M200-200q-17 0-28.5-11.5T160-240q0-17 11.5-28.5T200-280h40v-280q0-83 50-147.5T420-792v-28q0-25 17.5-42.5T480-880q25 0 42.5 17.5T540-820v28q16 4 30 9.5t28 13.5q20 11 23 27t-5 30q-8 14-23 20t-31-5q-18-11-38.5-17t-43.5-6q-66 0-113 47t-47 113v280h440q17 0 28.5 11.5T800-240q0 17-11.5 28.5T760-200H200Z"
                fill={color}
                opacity={isSoundOn ? 1 : 0.45}
            />

            {/* 3. Inner Acoustic Vibration Arc */}
            <motion.path
                d="M700-532q0 17-3 34t-10 33q-7 17-23 24t-32-1q-15-7-21-23t2-30q5-9 6-18t1-19q0-10-1.5-19.5T613-570q-6-14 0-28t20-21q15-7 30.5-.5T687-598q8 16 10.5 32.5T700-532Z"
                fill={color}
                animate={isSoundOn ? {
                    x: [0, 48, 0],
                    y: [0, -24, 0],
                    opacity: [0.35, 1, 0.4]
                } : { x: 0, y: 0, opacity: 0.2 }}
                transition={{
                    duration: 1.3,
                    repeat: Infinity,
                    ease: [0.22, 1, 0.36, 1]
                }}
            />

            {/* 4. Outer Acoustic Vibration Arc */}
            <motion.path
                d="M840-532q0 50-15 97t-45 87q-9 12-25.5 11.5T727-348q-11-11-12-27t8-30q19-28 28-60.5t9-66.5q0-34-9-66.5T723-659q-9-14-8-30t12-27q11-11 27.5-11.5T780-716q30 40 45 87t15 97Z"
                fill={color}
                animate={isSoundOn ? {
                    x: [0, 92, 0],
                    y: [0, -46, 0],
                    opacity: [0.18, 1, 0.22]
                } : { x: 0, y: 0, opacity: 0.12 }}
                transition={{
                    duration: 1.3,
                    repeat: Infinity,
                    delay: 0.2,
                    ease: [0.22, 1, 0.36, 1]
                }}
            />
        </svg>
    );
}

/**
 * NotificationBell - Real-Time HubSpot-Grade Notification Center & Matrix Preferences Studio
 */
export default function NotificationBell() {
    const { 
        notifications = [], 
        unreadCount = 0, 
        isRinging = false,
        markAllRead, 
        markAsRead, 
        markAsUnread,
        moveToTrash,
        restoreFromTrash,
        emptyTrash,
        deletePermanently,
        soundEnabled, 
        setSoundEnabled,
        selectedTone = 'apple-glass',
        setSelectedTone,
        availableTones = [],
        previewTone,
        desktopEnabled,
        desktopPermission,
        toggleDesktopNotifications,
        // Matrix
        notificationMatrix = [],
        notificationChannels = [],
        updateTopicChannel,
        toggleCategoryChannel,
        toggleMasterChannel,
        turnOffAllTopics,
        resetChannelDefaults
    } = useNotifications();

    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    
    // View state: 'inbox' (HubSpot Screenshot 1) | 'preferences' (HubSpot Screenshot 2) | 'tones' (Sound Studio)
    const [viewMode, setViewMode] = useState('inbox'); 
    const [toneFilter, setToneFilter] = useState('all');

    // Inbox Tabs (HubSpot Screenshot 1): 'unread' | 'all' | 'trash'
    const [activeTab, setActiveTab] = useState('unread');
    
    // Type Filter (HubSpot Screenshot 1: 'All', 'Mention', etc.)
    const [typeFilter, setTypeFilter] = useState('all');
    const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);

    // Multi-select state
    const [selectedIds, setSelectedIds] = useState([]);

    // Preferences Matrix search & filters (HubSpot Screenshot 2)
    const [matrixSearch, setMatrixSearch] = useState('');
    const [matrixStatusFilter, setMatrixStatusFilter] = useState('all'); // 'all' | 'enabled' | 'disabled'
    const [expandedCategories, setExpandedCategories] = useState(() => ({
        academy: true,
        account: true,
        account_settings: true,
        qms: true,
        sales: true,
        mentions: true
    }));

    // Tooltip info state
    const [showInfoTooltip, setShowInfoTooltip] = useState(false);

    // Toggling expand all / collapse all
    const handleToggleAllTopics = () => {
        const allExpanded = Object.values(expandedCategories).every(v => v);
        const nextState = !allExpanded;
        const updated = {};
        notificationMatrix.forEach(cat => {
            updated[cat.id] = nextState;
        });
        setExpandedCategories(updated);
    };

    const toggleCategoryExpand = (catId) => {
        setExpandedCategories(prev => ({
            ...prev,
            [catId]: !prev[catId]
        }));
    };

    // Filter notifications for current view
    const visibleNotifications = useMemo(() => {
        return notifications.filter(n => {
            if (activeTab === 'trash') {
                if (!n.trashed) return false;
            } else {
                if (n.trashed) return false;
                if (activeTab === 'unread' && n.read) return false;
            }

            if (typeFilter !== 'all') {
                if (typeFilter === 'mention' && n.type !== 'mention' && n.app !== 'mentions') return false;
                if (typeFilter === 'alert' && n.type !== 'alert' && n.type !== 'system') return false;
                if (typeFilter === 'qms' && n.app !== 'qms') return false;
                if (typeFilter === 'sales' && n.app !== 'sales') return false;
            }

            return true;
        });
    }, [notifications, activeTab, typeFilter]);

    // Trash counts
    const trashCount = useMemo(() => {
        return notifications.filter(n => n.trashed).length;
    }, [notifications]);

    const allInboxCount = useMemo(() => {
        return notifications.filter(n => !n.trashed).length;
    }, [notifications]);

    // Selection handlers
    const isAllSelected = visibleNotifications.length > 0 && selectedIds.length === visibleNotifications.length;

    const handleToggleSelectAll = () => {
        if (isAllSelected) {
            setSelectedIds([]);
        } else {
            setSelectedIds(visibleNotifications.map(n => n.id));
        }
    };

    const handleToggleSelectOne = (id, e) => {
        e?.stopPropagation();
        setSelectedIds(prev => 
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const handleNotifClick = (notif) => {
        if (!notif.read && !notif.trashed) {
            markAsRead([notif.id]);
        }
        if (notif.link) {
            setIsOpen(false);
            navigate(notif.link);
        }
    };

    // Bulk actions
    const handleBulkMarkRead = () => {
        if (selectedIds.length === 0) return;
        markAsRead(selectedIds);
        setSelectedIds([]);
    };

    const handleBulkMoveToTrash = () => {
        if (selectedIds.length === 0) return;
        moveToTrash(selectedIds);
        setSelectedIds([]);
    };

    const handleBulkRestore = () => {
        if (selectedIds.length === 0) return;
        restoreFromTrash(selectedIds);
        setSelectedIds([]);
    };

    const handleBulkDeletePermanently = () => {
        if (selectedIds.length === 0) return;
        deletePermanently(selectedIds);
        setSelectedIds([]);
    };

    // Filtered Matrix Topics
    const filteredMatrix = useMemo(() => {
        const query = matrixSearch.trim().toLowerCase();
        return notificationMatrix.map(cat => {
            const matchesCat = cat.name.toLowerCase().includes(query) || (cat.description || '').toLowerCase().includes(query);
            const matchingSubtopics = cat.subtopics.filter(sub => {
                const matchesSub = matchesCat || sub.name.toLowerCase().includes(query) || (sub.desc || '').toLowerCase().includes(query);
                if (!matchesSub) return false;

                if (matrixStatusFilter === 'enabled') {
                    return Object.values(sub.channels).some(v => v === true);
                } else if (matrixStatusFilter === 'disabled') {
                    return Object.values(sub.channels).every(v => v !== true);
                }
                return true;
            });

            return {
                ...cat,
                isMatch: matchesCat || matchingSubtopics.length > 0,
                filteredSubtopics: matchingSubtopics
            };
        }).filter(cat => cat.filteredSubtopics.length > 0);
    }, [notificationMatrix, matrixSearch, matrixStatusFilter]);

    return (
        <div className="relative">
            {/* ── Bell Trigger Button with Real-Time Ring Animation ────────── */}
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className={`relative w-[34px] h-[34px] rounded-lg border-none cursor-pointer flex items-center justify-center transition-all p-0 ${
                    isOpen 
                        ? 'bg-[var(--accent-subtle)] text-[var(--accent)] shadow-2xs' 
                        : 'bg-transparent hover:bg-[var(--bg-muted)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                }`}
                title="Notifications"
            >
                <motion.div
                    animate={
                        isRinging 
                            ? { rotate: [0, -22, 22, -16, 16, -10, 10, -4, 4, 0], scale: [1, 1.15, 1.1, 1.05, 1] }
                            : isHovered && !isOpen 
                                ? { rotate: [0, -12, 12, -8, 8, -4, 4, 0] } 
                                : { rotate: 0 }
                    }
                    transition={{ duration: isRinging ? 0.9 : 0.5, ease: "easeInOut" }}
                    className="flex items-center justify-center"
                >
                    <ObliqueIcon 
                        name="notifications" 
                        size={20} 
                        color={isOpen ? "var(--accent)" : "currentColor"}
                        filled={isOpen || unreadCount > 0}
                    />
                </motion.div>
                
                {unreadCount > 0 && (
                    <motion.span 
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        className="absolute -top-0.5 -right-0.5 min-w-[17px] h-[17px] rounded-full bg-[#ff5c35] text-white text-[10px] font-bold flex items-center justify-center px-1 shadow-2xs border-2 border-[var(--bg-surface)] leading-none"
                    >
                        {unreadCount > 99 ? '99+' : unreadCount}
                    </motion.span>
                )}
            </button>

            {/* ── Slide-in Side Drawer (HubSpot Notifications & Studio) ───── */}
            {typeof document !== 'undefined' && createPortal(
                <div style={{ position: 'fixed', zIndex: 9999 }}>
                    <AnimatePresence>
                        {isOpen && (
                            <>
                                {/* Dimmed Backdrop */}
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.18 }}
                                    onClick={() => { setIsOpen(false); setSelectedIds([]); }}
                                    className="fixed inset-0 bg-black/40 z-[9998]"
                                />

                                {/* Slide-in Drawer Panel (Hardware-accelerated, zero-lag) */}
                                <motion.div 
                                    initial={{ x: '100%' }}
                                    animate={{ x: 0 }}
                                    exit={{ x: '100%' }}
                                    transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
                                    style={{ willChange: 'transform' }}
                                    className={`fixed top-0 right-0 bottom-0 bg-[var(--bg-surface)] border-l border-[var(--border)] shadow-[0_0_50px_rgba(0,0,0,0.22)] z-[9999] flex flex-col overflow-hidden ${
                                        viewMode === 'preferences' ? 'w-[780px] max-w-[100vw]' : 'w-[520px] max-w-[100vw]'
                                    }`}
                                >
                                    {/* ══════════════════════════════════════════════════════════════════ */}
                                    {/* VIEW 1: NOTIFICATIONS DRAWER (HUB-SPOT SCREENSHOT 1)             */}
                                    {/* ══════════════════════════════════════════════════════════════════ */}
                                    {viewMode === 'inbox' && (
                                        <>
                                            {/* 1. Header Bar: "Notifications" + Close (×) */}
                                            <div className="px-6 pt-5 pb-3 flex items-center justify-between bg-[var(--bg-surface)]">
                                                <div className="flex items-center gap-2">
                                                    <h2 className="text-[19px] font-bold tracking-tight text-[var(--text-primary)] m-0">
                                                        Notifications
                                                    </h2>
                                                    {unreadCount > 0 && activeTab === 'unread' && (
                                                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[#ff5c35]/10 text-[#ff5c35]">
                                                            {unreadCount} new
                                                        </span>
                                                    )}
                                                </div>

                                                <button
                                                    type="button"
                                                    onClick={() => setIsOpen(false)}
                                                    className="w-8 h-8 rounded-lg flex items-center justify-center bg-transparent border-none cursor-pointer text-[var(--text-tertiary)] hover:bg-[var(--bg-muted)] hover:text-[var(--text-primary)] transition-colors p-0"
                                                    title="Close"
                                                >
                                                    <ObliqueIcon name="close" size={20} />
                                                </button>
                                            </div>

                                            {/* 2. Tabs Row: Unread (1) | All | Trash | [Gear Icon] */}
                                            <div className="px-6 flex items-center justify-between border-b border-[var(--border-subtle)] bg-[var(--bg-surface)]">
                                                <div className="flex items-center gap-6">
                                                    {[
                                                        { id: 'unread', label: `Unread (${unreadCount})` },
                                                        { id: 'all', label: `All (${allInboxCount})` },
                                                        { id: 'trash', label: `Trash (${trashCount})` }
                                                    ].map(tab => {
                                                        const isActive = activeTab === tab.id;
                                                        return (
                                                            <button
                                                                key={tab.id}
                                                                type="button"
                                                                onClick={() => {
                                                                    setActiveTab(tab.id);
                                                                    setSelectedIds([]);
                                                                }}
                                                                className={`relative pb-3 pt-1 text-[13.5px] cursor-pointer border-none bg-transparent transition-colors font-medium flex items-center gap-1.5 ${
                                                                    isActive 
                                                                        ? 'text-[var(--text-primary)] font-semibold' 
                                                                        : 'text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]'
                                                                }`}
                                                            >
                                                                <span>{tab.label}</span>
                                                                {/* Dark sleek underline indicator */}
                                                                {isActive && (
                                                                    <motion.div
                                                                        layoutId="hubspot-active-tab-indicator"
                                                                        className="absolute bottom-0 left-0 right-0 h-[2.5px] bg-[var(--text-primary)] rounded-full"
                                                                    />
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>

                                                {/* Settings Gear Icon (Opens Notification Preferences Studio) */}
                                                <button
                                                    type="button"
                                                    onClick={() => setViewMode('preferences')}
                                                    className="w-8 h-8 rounded-lg flex items-center justify-center bg-transparent border-none cursor-pointer text-[var(--text-tertiary)] hover:bg-[var(--bg-muted)] hover:text-[var(--text-primary)] transition-colors p-0 mb-1.5"
                                                    title="Notification Preferences"
                                                >
                                                    <ObliqueIcon name="settings" size={19} />
                                                </button>
                                            </div>

                                            {/* 3. Sub-bar: [ ] Select all ⓘ        Type: All ▾ */}
                                            <div className="px-6 py-2.5 flex items-center justify-between bg-[var(--bg-surface)] border-b border-[var(--border-subtle)]">
                                                {/* Left: Checkbox + Select all ⓘ */}
                                                <div className="flex items-center gap-3">
                                                    <label className="flex items-center gap-2 cursor-pointer select-none">
                                                        <input 
                                                            type="checkbox"
                                                            checked={isAllSelected}
                                                            onChange={handleToggleSelectAll}
                                                            className="w-4 h-4 rounded border-[var(--border)] text-[var(--accent)] focus:ring-0 cursor-pointer accent-[var(--accent)]"
                                                        />
                                                        <span className="text-[13px] font-medium text-[var(--text-secondary)]">
                                                            Select all
                                                        </span>
                                                    </label>

                                                    {/* Info Tooltip Icon */}
                                                    <div className="relative inline-block">
                                                        <button
                                                            type="button"
                                                            onMouseEnter={() => setShowInfoTooltip(true)}
                                                            onMouseLeave={() => setShowInfoTooltip(false)}
                                                            onClick={() => setShowInfoTooltip(!showInfoTooltip)}
                                                            className="w-4 h-4 rounded-full flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)] bg-transparent border-none cursor-pointer p-0"
                                                        >
                                                            <ObliqueIcon name="info" size={14} />
                                                        </button>
                                                        {showInfoTooltip && (
                                                            <div className="absolute left-6 top-1/2 -translate-y-1/2 w-52 p-2 bg-[var(--bg-surface)] border border-[var(--border)] shadow-lg rounded-lg text-[11px] text-[var(--text-secondary)] z-50 pointer-events-none">
                                                                Select all notifications currently visible to mark them as read or move them to trash.
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Right: Actions or Filter Dropdown */}
                                                {selectedIds.length > 0 ? (
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-[12px] font-medium text-[var(--text-tertiary)] mr-1">
                                                            {selectedIds.length} selected
                                                        </span>
                                                        {activeTab === 'trash' ? (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    onClick={handleBulkRestore}
                                                                    className="px-2.5 py-1 text-xs font-medium rounded-md bg-[var(--bg-muted)] hover:bg-[var(--border-subtle)] text-[var(--text-primary)] border-none cursor-pointer flex items-center gap-1 transition-colors"
                                                                >
                                                                    <ObliqueIcon name="restore_from_trash" size={14} />
                                                                    <span>Restore</span>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={handleBulkDeletePermanently}
                                                                    className="px-2.5 py-1 text-xs font-medium rounded-md bg-rose-50 hover:bg-rose-100 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400 border-none cursor-pointer flex items-center gap-1 transition-colors"
                                                                >
                                                                    <ObliqueIcon name="delete_forever" size={14} />
                                                                    <span>Delete</span>
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    onClick={handleBulkMarkRead}
                                                                    className="px-2.5 py-1 text-xs font-medium rounded-md bg-[var(--bg-muted)] hover:bg-[var(--border-subtle)] text-[var(--text-primary)] border-none cursor-pointer flex items-center gap-1 transition-colors"
                                                                >
                                                                    <ObliqueIcon name="done" size={14} />
                                                                    <span>Mark read</span>
                                                                </button>
                                                                <button
                                                                    type="button"
                                                                    onClick={handleBulkMoveToTrash}
                                                                    className="px-2.5 py-1 text-xs font-medium rounded-md bg-[var(--bg-muted)] hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40 dark:hover:text-rose-400 text-[var(--text-primary)] border-none cursor-pointer flex items-center gap-1 transition-colors"
                                                                >
                                                                    <ObliqueIcon name="delete" size={14} />
                                                                    <span>Trash</span>
                                                                </button>
                                                            </>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        {activeTab === 'trash' && trashCount > 0 && (
                                                            <button
                                                                type="button"
                                                                onClick={emptyTrash}
                                                                className="px-2 py-0.5 text-[11.5px] font-medium text-rose-600 dark:text-rose-400 hover:underline bg-transparent border-none cursor-pointer"
                                                            >
                                                                Empty Trash
                                                            </button>
                                                        )}

                                                        {/* Type Filter Popover Button */}
                                                        <div className="relative">
                                                            <button
                                                                type="button"
                                                                onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
                                                                className="text-[13px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] bg-transparent border-none cursor-pointer flex items-center gap-1 py-1 px-1.5 rounded transition-colors"
                                                            >
                                                                <span>Type:</span>
                                                                <span className="font-semibold text-[var(--text-primary)] capitalize">
                                                                    {typeFilter === 'all' ? 'All' : typeFilter}
                                                                </span>
                                                                <ObliqueIcon name="arrow_drop_down" size={16} />
                                                            </button>

                                                            {/* Dropdown Menu matching Screenshot 1 */}
                                                            {isTypeDropdownOpen && (
                                                                <>
                                                                    <div 
                                                                        className="fixed inset-0 z-40" 
                                                                        onClick={() => setIsTypeDropdownOpen(false)} 
                                                                    />
                                                                    <div className="absolute right-0 top-full mt-1.5 w-36 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl py-1 z-50 text-[13px] overflow-hidden">
                                                                        {/* Decorative Caret Triangle */}
                                                                        <div className="absolute -top-1.5 right-4 w-3 h-3 bg-[var(--bg-surface)] border-t border-l border-[var(--border)] rotate-45" />

                                                                        {[
                                                                            { id: 'all', label: 'All' },
                                                                            { id: 'mention', label: 'Mention' },
                                                                            { id: 'alert', label: 'Alerts' },
                                                                            { id: 'qms', label: 'QMS' },
                                                                            { id: 'sales', label: 'Sales' }
                                                                        ].map(opt => (
                                                                            <button
                                                                                key={opt.id}
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setTypeFilter(opt.id);
                                                                                    setIsTypeDropdownOpen(false);
                                                                                }}
                                                                                className={`w-full text-left px-3.5 py-2 border-none cursor-pointer flex items-center justify-between text-[13px] transition-colors ${
                                                                                    typeFilter === opt.id 
                                                                                        ? 'bg-[var(--accent-subtle)] text-[var(--accent)] font-semibold' 
                                                                                        : 'bg-transparent text-[var(--text-primary)] hover:bg-[var(--bg-muted)]'
                                                                                }`}
                                                                            >
                                                                                <span>{opt.label}</span>
                                                                                {typeFilter === opt.id && (
                                                                                    <ObliqueIcon name="check" size={14} color="var(--accent)" />
                                                                                )}
                                                                            </button>
                                                                        ))}
                                                                    </div>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>

                                            {/* 4. Notification Items List */}
                                            <div className="flex-1 overflow-y-auto custom-scrollbar">
                                                {visibleNotifications.length === 0 ? (
                                                    <div className="py-24 px-6 text-center flex flex-col items-center justify-center">
                                                        <div className="w-12 h-12 rounded-2xl bg-[var(--bg-subtle)] border border-[var(--border-subtle)] flex items-center justify-center mb-3 text-[var(--text-tertiary)]">
                                                            <ObliqueIcon 
                                                                name={activeTab === 'trash' ? "delete_outline" : "done_all"} 
                                                                size={24} 
                                                                color="var(--accent)" 
                                                            />
                                                        </div>
                                                        <h3 className="text-sm font-semibold text-[var(--text-primary)] mb-1">
                                                            {activeTab === 'trash' ? 'Trash is empty' : 'All caught up'}
                                                        </h3>
                                                        <p className="text-xs text-[var(--text-secondary)] max-w-xs leading-relaxed">
                                                            {activeTab === 'trash' 
                                                                ? 'Notifications you move to trash will show here before permanent removal.' 
                                                                : `No ${activeTab === 'unread' ? 'unread ' : ''}notifications at this time.`
                                                            }
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div className="divide-y divide-[var(--border-subtle)]">
                                                        {visibleNotifications.map(notif => {
                                                            const isSelected = selectedIds.includes(notif.id);
                                                            const isUnread = !notif.read && !notif.trashed;

                                                            return (
                                                                <div
                                                                    key={notif.id}
                                                                    onClick={() => handleNotifClick(notif)}
                                                                    className={`relative px-6 py-3.5 flex items-start gap-3.5 transition-colors cursor-pointer group ${
                                                                        isUnread 
                                                                            ? 'bg-[var(--bg-surface)] hover:bg-[var(--bg-subtle)]' 
                                                                            : 'bg-transparent hover:bg-[var(--bg-subtle)] opacity-85 hover:opacity-100'
                                                                    }`}
                                                                >
                                                                        {/* Unread Accent Indicator on far left */}
                                                                        {isUnread && (
                                                                            <div className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r bg-[#ff5c35]" />
                                                                        )}

                                                                        {/* Left Checkbox */}
                                                                        <div 
                                                                            className="pt-0.5 shrink-0"
                                                                            onClick={(e) => handleToggleSelectOne(notif.id, e)}
                                                                        >
                                                                            <input 
                                                                                type="checkbox"
                                                                                checked={isSelected}
                                                                                onChange={() => {}}
                                                                                className="w-4 h-4 rounded border-[var(--border)] text-[var(--accent)] focus:ring-0 cursor-pointer accent-[var(--accent)]"
                                                                            />
                                                                        </div>

                                                                        {/* Center Content */}
                                                                        <div className="flex-1 min-w-0 pr-1">
                                                                            {/* Title (Bold, HubSpot format) */}
                                                                            <div className="text-[13px] font-semibold text-[var(--text-primary)] leading-snug">
                                                                                {cleanTitle(notif.title)}
                                                                            </div>

                                                                            {/* Subtitle / Message snippet */}
                                                                            {notif.message && (
                                                                                <div className="text-[12px] text-[var(--text-secondary)] leading-relaxed mt-0.5 line-clamp-2">
                                                                                    {notif.message}
                                                                                </div>
                                                                            )}

                                                                            {/* Pill Badge (e.g. silkyhairskincare.net) */}
                                                                            {notif.entityDomain && (
                                                                                <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-[var(--bg-subtle)] border border-[var(--border-subtle)] text-[11.5px] font-medium text-[var(--text-primary)] hover:border-[var(--accent)]/40 transition-colors">
                                                                                    <ObliqueIcon name="language" size={13} color="var(--text-secondary)" />
                                                                                    <span>{notif.entityDomain}</span>
                                                                                </div>
                                                                            )}
                                                                        </div>

                                                                        {/* Right Timestamp & Hover Actions */}
                                                                        <div className="flex flex-col items-end shrink-0 pl-2">
                                                                            <span className="text-[11.5px] text-[var(--text-tertiary)] mb-1 whitespace-nowrap">
                                                                                {formatTime(notif.time)}
                                                                            </span>

                                                                            {/* Quick Action Icons on Hover */}
                                                                            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 mt-1">
                                                                                {activeTab === 'trash' ? (
                                                                                    <>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                restoreFromTrash([notif.id]);
                                                                                            }}
                                                                                            title="Restore notification"
                                                                                            className="w-6 h-6 rounded flex items-center justify-center border-none bg-transparent hover:bg-[var(--bg-muted)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] cursor-pointer transition-colors p-0"
                                                                                        >
                                                                                            <ObliqueIcon name="restore_from_trash" size={14} />
                                                                                        </button>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                deletePermanently([notif.id]);
                                                                                            }}
                                                                                            title="Delete permanently"
                                                                                            className="w-6 h-6 rounded flex items-center justify-center border-none bg-transparent hover:bg-[var(--bg-muted)] text-[var(--text-tertiary)] hover:text-rose-500 cursor-pointer transition-colors p-0"
                                                                                        >
                                                                                            <ObliqueIcon name="delete_forever" size={14} />
                                                                                        </button>
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                if (notif.read) {
                                                                                                    markAsUnread([notif.id]);
                                                                                                } else {
                                                                                                    markAsRead([notif.id]);
                                                                                                }
                                                                                            }}
                                                                                            title={notif.read ? "Mark as unread" : "Mark as read"}
                                                                                            className="w-6 h-6 rounded flex items-center justify-center border-none bg-transparent hover:bg-[var(--bg-muted)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] cursor-pointer transition-colors p-0"
                                                                                        >
                                                                                            <ObliqueIcon name={notif.read ? "mark_chat_unread" : "done"} size={14} />
                                                                                        </button>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={(e) => {
                                                                                                e.stopPropagation();
                                                                                                moveToTrash([notif.id]);
                                                                                            }}
                                                                                            title="Move to trash"
                                                                                            className="w-6 h-6 rounded flex items-center justify-center border-none bg-transparent hover:bg-[var(--bg-muted)] text-[var(--text-tertiary)] hover:text-rose-500 cursor-pointer transition-colors p-0"
                                                                                        >
                                                                                            <ObliqueIcon name="delete" size={14} />
                                                                                        </button>
                                                                                        {notif.link && (
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={(e) => {
                                                                                                    e.stopPropagation();
                                                                                                    handleNotifClick(notif);
                                                                                                }}
                                                                                                title="Open link"
                                                                                                className="w-6 h-6 rounded flex items-center justify-center border-none bg-transparent hover:bg-[var(--bg-muted)] text-[var(--text-tertiary)] hover:text-[var(--accent)] cursor-pointer transition-colors p-0"
                                                                                            >
                                                                                                <ObliqueIcon name="arrow_outward" size={14} />
                                                                                            </button>
                                                                                        )}
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Bottom bar quick action */}
                                            {unreadCount > 0 && activeTab !== 'trash' && (
                                                <div className="px-6 py-2.5 bg-[var(--bg-subtle)] border-t border-[var(--border-subtle)] flex items-center justify-between">
                                                    <span className="text-[12px] text-[var(--text-secondary)]">
                                                        {unreadCount} unread notification{unreadCount > 1 ? 's' : ''}
                                                    </span>
                                                    <button
                                                        type="button"
                                                        onClick={markAllRead}
                                                        className="text-[12px] font-medium text-[var(--accent)] hover:underline bg-transparent border-none cursor-pointer flex items-center gap-1 p-0"
                                                    >
                                                        <ObliqueIcon name="done_all" size={14} />
                                                        <span>Mark all as read</span>
                                                    </button>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    {/* ══════════════════════════════════════════════════════════════════ */}
                                    {/* VIEW 2: NOTIFICATION PREFERENCES MATRIX (HUB-SPOT SCREENSHOT 2)   */}
                                    {/* ══════════════════════════════════════════════════════════════════ */}
                                    {viewMode === 'preferences' && (
                                        <div className="flex-1 flex flex-col overflow-hidden bg-[var(--bg-surface)]">
                                            {/* Top Sub-Header: Back Navigation + "Set channel defaults" */}
                                            <div className="px-7 pt-5 pb-3 border-b border-[var(--border-subtle)]">
                                                <div className="flex items-center justify-between mb-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => setViewMode('inbox')}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--bg-subtle)] hover:bg-[var(--bg-muted)] text-[12px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors border border-[var(--border-subtle)] cursor-pointer"
                                                    >
                                                        <ObliqueIcon name="arrow_back" size={15} />
                                                        <span>Back to Notifications</span>
                                                    </button>

                                                    <div className="flex items-center gap-2">
                                                        {/* Acoustic Studio Trigger */}
                                                        <button
                                                            type="button"
                                                            onClick={() => setViewMode('tones')}
                                                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[var(--bg-subtle)] hover:bg-[var(--bg-muted)] border border-[var(--border-subtle)] hover:border-[var(--accent)]/40 text-[12px] font-medium transition-all cursor-pointer group"
                                                            title="Configure Sound & Tone"
                                                        >
                                                            <NotificationSoundVibrationIcon isSoundOn={soundEnabled} size={15} color="var(--accent)" />
                                                            <span className="text-[var(--text-primary)] font-medium">
                                                                Sound & Tone Studio
                                                            </span>
                                                            <ObliqueIcon name="chevron_right" size={14} color="var(--text-tertiary)" />
                                                        </button>

                                                        {/* Close Button */}
                                                        <button
                                                            type="button"
                                                            onClick={() => setIsOpen(false)}
                                                            className="w-8 h-8 rounded-lg flex items-center justify-center bg-transparent border-none cursor-pointer text-[var(--text-tertiary)] hover:bg-[var(--bg-muted)] hover:text-[var(--text-primary)] transition-colors p-0"
                                                        >
                                                            <ObliqueIcon name="close" size={20} />
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="flex items-start justify-between gap-4 mt-2">
                                                    <div>
                                                        <h1 className="text-[22px] font-bold tracking-tight text-[var(--text-primary)] m-0">
                                                            Notifications
                                                        </h1>
                                                        <p className="text-[13px] text-[var(--text-secondary)] mt-1 mb-0 leading-relaxed">
                                                            Control how and when you receive notifications. These preferences apply only to you.
                                                        </p>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            resetChannelDefaults();
                                                        }}
                                                        className="px-3.5 py-1.5 rounded-lg border border-[var(--border)] bg-[var(--bg-surface)] hover:bg-[var(--bg-muted)] text-[12.5px] font-medium text-[var(--text-primary)] cursor-pointer transition-all flex items-center gap-1.5 shrink-0 shadow-2xs"
                                                    >
                                                        <ObliqueIcon name="tune" size={15} />
                                                        <span>Set channel defaults</span>
                                                    </button>
                                                </div>
                                            </div>

                                            {/* Scrollable Matrix Content */}
                                            <div className="flex-1 overflow-y-auto custom-scrollbar px-7 py-5 space-y-6">
                                                {/* Search Bar matching HubSpot */}
                                                <div className="relative">
                                                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] flex items-center pointer-events-none">
                                                        <ObliqueIcon name="search" size={18} />
                                                    </div>
                                                    <input 
                                                        type="text"
                                                        value={matrixSearch}
                                                        onChange={(e) => setMatrixSearch(e.target.value)}
                                                        placeholder='Search for a topic, for example "tickets"'
                                                        className="w-full pl-11 pr-10 py-3 rounded-xl bg-[var(--bg-subtle)] border border-[var(--border)] text-[13.5px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[var(--accent)] transition-all shadow-2xs"
                                                    />
                                                    {matrixSearch && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setMatrixSearch('')}
                                                            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] hover:text-[var(--text-primary)] bg-transparent border-none cursor-pointer p-1"
                                                        >
                                                            <ObliqueIcon name="close" size={16} />
                                                        </button>
                                                    )}
                                                </div>

                                                {/* Section Header: All notifications + Filter + Quick Actions */}
                                                <div>
                                                    <div className="flex items-center justify-between pb-3">
                                                        <div>
                                                            <h3 className="text-[16px] font-bold text-[var(--text-primary)] m-0">
                                                                All notifications
                                                            </h3>
                                                            <div className="flex items-center gap-1 mt-1 text-[12.5px] text-[var(--text-secondary)]">
                                                                <span>Notification status</span>
                                                                <select
                                                                    value={matrixStatusFilter}
                                                                    onChange={(e) => setMatrixStatusFilter(e.target.value)}
                                                                    className="bg-transparent border-none text-[var(--text-primary)] font-semibold cursor-pointer outline-none pl-1"
                                                                >
                                                                    <option value="all">All</option>
                                                                    <option value="enabled">Turned on</option>
                                                                    <option value="disabled">Turned off</option>
                                                                </select>
                                                            </div>
                                                        </div>

                                                        {/* Expand all topics | Turn off all topics ⓘ */}
                                                        <div className="flex items-center gap-2 text-[12.5px]">
                                                            <button
                                                                type="button"
                                                                onClick={handleToggleAllTopics}
                                                                className="text-[#007a87] dark:text-teal-400 font-medium hover:underline bg-transparent border-none cursor-pointer p-0"
                                                            >
                                                                {Object.values(expandedCategories).every(v => v) ? 'Collapse all topics' : 'Expand all topics'}
                                                            </button>
                                                            <span className="text-[var(--text-tertiary)]">|</span>
                                                            <button
                                                                type="button"
                                                                onClick={turnOffAllTopics}
                                                                className="text-[#007a87] dark:text-teal-400 font-medium hover:underline bg-transparent border-none cursor-pointer p-0 flex items-center gap-1"
                                                            >
                                                                <span>Turn off all topics</span>
                                                                <ObliqueIcon name="info" size={13} />
                                                            </button>
                                                        </div>
                                                    </div>

                                                    {/* Matrix Table Header Columns with Channel Icons & Master Toggles */}
                                                    <div className="grid grid-cols-12 gap-2 pt-3 pb-2 px-4 border-b border-[var(--border-subtle)] text-[12px] font-medium text-[var(--text-secondary)]">
                                                        <div className="col-span-5 font-semibold text-[var(--text-primary)]">
                                                            Topic
                                                        </div>

                                                        {/* 6 Channel Columns */}
                                                        {notificationChannels.map(ch => (
                                                            <div key={ch.id} className="col-span-1 flex flex-col items-center justify-center gap-1 text-center">
                                                                <ObliqueIcon name={ch.icon} size={16} color="var(--text-secondary)" />
                                                                <span className="text-[11px] leading-tight">{ch.label}</span>
                                                                
                                                                {/* Master Column Toggle Icon */}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => toggleMasterChannel(ch.id)}
                                                                    className="w-5 h-5 rounded flex items-center justify-center bg-[var(--bg-muted)] hover:bg-[var(--border-subtle)] border border-[var(--border-subtle)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] cursor-pointer mt-0.5 transition-colors p-0"
                                                                    title={`Toggle all ${ch.label} notifications`}
                                                                >
                                                                    <ObliqueIcon name="remove" size={12} />
                                                                </button>
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {/* Accordion Categories Matching HubSpot Screenshot 2 */}
                                                    <div className="space-y-3 mt-3">
                                                        {filteredMatrix.map(cat => {
                                                            const isExpanded = Boolean(expandedCategories[cat.id]);

                                                            return (
                                                                <div 
                                                                    key={cat.id} 
                                                                    className="rounded-xl border border-[var(--border)] bg-[var(--bg-surface)] overflow-hidden shadow-2xs transition-all"
                                                                >
                                                                    {/* Category Row Header */}
                                                                    <div 
                                                                        onClick={() => toggleCategoryExpand(cat.id)}
                                                                        className="grid grid-cols-12 gap-2 px-4 py-3.5 items-center cursor-pointer hover:bg-[var(--bg-subtle)] transition-colors select-none"
                                                                    >
                                                                        {/* Left Category Title with Chevron */}
                                                                        <div className="col-span-5 flex items-center gap-2.5">
                                                                            <motion.div
                                                                                animate={{ rotate: isExpanded ? 90 : 0 }}
                                                                                transition={{ duration: 0.15 }}
                                                                                className="text-[var(--text-tertiary)] flex items-center"
                                                                            >
                                                                                <ObliqueIcon name="chevron_right" size={17} />
                                                                            </motion.div>
                                                                            <span className="text-[13.5px] font-semibold text-[var(--text-primary)]">
                                                                                {cat.name}
                                                                            </span>
                                                                        </div>

                                                                        {/* 6 Category Channel Aggregate Indicators */}
                                                                        {notificationChannels.map(ch => {
                                                                            const applicable = cat.subtopics.filter(s => s.channels[ch.id] !== null);
                                                                            const enabledCount = applicable.filter(s => Boolean(s.channels[ch.id])).length;
                                                                            const isSupported = applicable.length > 0;
                                                                            const allOn = isSupported && enabledCount === applicable.length;
                                                                            const partial = isSupported && enabledCount > 0 && enabledCount < applicable.length;

                                                                            return (
                                                                                <div 
                                                                                    key={ch.id} 
                                                                                    className="col-span-1 flex items-center justify-center"
                                                                                    onClick={(e) => {
                                                                                        e.stopPropagation();
                                                                                        if (isSupported) {
                                                                                            toggleCategoryChannel(cat.id, ch.id);
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    {!isSupported ? (
                                                                                        <span className="text-xs text-[var(--text-tertiary)] font-mono">--</span>
                                                                                    ) : allOn ? (
                                                                                        <div className="w-4 h-4 rounded border border-[var(--accent)] bg-[var(--accent)] text-white flex items-center justify-center cursor-pointer">
                                                                                            <ObliqueIcon name="check" size={11} color="white" />
                                                                                        </div>
                                                                                    ) : partial ? (
                                                                                        <div className="w-4 h-4 rounded border border-[var(--border)] bg-[var(--bg-muted)] text-[var(--text-secondary)] flex items-center justify-center cursor-pointer">
                                                                                            <ObliqueIcon name="remove" size={12} />
                                                                                        </div>
                                                                                    ) : (
                                                                                        <div className="w-4 h-4 rounded border border-[var(--border)] bg-[var(--bg-surface)] hover:border-[var(--accent)] cursor-pointer transition-colors" />
                                                                                    )}
                                                                                </div>
                                                                            );
                                                                        })}
                                                                    </div>

                                                                    {/* Expandable Subtopics List */}
                                                                    <AnimatePresence>
                                                                        {isExpanded && (
                                                                            <motion.div
                                                                                initial={{ opacity: 0, height: 0 }}
                                                                                animate={{ opacity: 1, height: 'auto' }}
                                                                                exit={{ opacity: 0, height: 0 }}
                                                                                className="border-t border-[var(--border-subtle)] bg-[var(--bg-subtle)]/40 divide-y divide-[var(--border-subtle)]"
                                                                            >
                                                                                {cat.filteredSubtopics.map(sub => (
                                                                                    <div 
                                                                                        key={sub.id}
                                                                                        className="grid grid-cols-12 gap-2 px-4 py-2.5 items-center hover:bg-[var(--bg-muted)]/50 transition-colors"
                                                                                    >
                                                                                        {/* Subtopic Title & Description */}
                                                                                        <div className="col-span-5 pl-7">
                                                                                            <div className="text-[12.5px] font-medium text-[var(--text-primary)]">
                                                                                                {sub.name}
                                                                                            </div>
                                                                                            {sub.desc && (
                                                                                                <div className="text-[11px] text-[var(--text-tertiary)] line-clamp-1">
                                                                                                    {sub.desc}
                                                                                                </div>
                                                                                            )}
                                                                                        </div>

                                                                                        {/* Subtopic Checkboxes across all 6 Channels */}
                                                                                        {notificationChannels.map(ch => {
                                                                                            const val = sub.channels[ch.id];
                                                                                            const isSupported = val !== null;

                                                                                            return (
                                                                                                <div key={ch.id} className="col-span-1 flex items-center justify-center">
                                                                                                    {!isSupported ? (
                                                                                                        <span className="text-xs text-[var(--text-tertiary)] font-mono">--</span>
                                                                                                    ) : (
                                                                                                        <input 
                                                                                                            type="checkbox"
                                                                                                            checked={Boolean(val)}
                                                                                                            onChange={(e) => updateTopicChannel(cat.id, sub.id, ch.id, e.target.checked)}
                                                                                                            className="w-4 h-4 rounded border-[var(--border)] text-[var(--accent)] focus:ring-0 cursor-pointer accent-[var(--accent)]"
                                                                                                        />
                                                                                                    )}
                                                                                                </div>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                ))}
                                                                            </motion.div>
                                                                        )}
                                                                    </AnimatePresence>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* ══════════════════════════════════════════════════════════════════ */}
                                    {/* VIEW 3: SOUND & TONE STUDIO (ACOUSTIC ENGINE)                    */}
                                    {/* ══════════════════════════════════════════════════════════════════ */}
                                    {viewMode === 'tones' && (
                                        <div className="flex-1 flex flex-col overflow-hidden bg-[var(--bg-surface)]">
                                            {/* Header */}
                                            <div className="px-6 py-4 border-b border-[var(--border-subtle)] flex items-center justify-between">
                                                <button
                                                    type="button"
                                                    onClick={() => setViewMode('preferences')}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[var(--bg-subtle)] hover:bg-[var(--bg-muted)] text-[12px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors border border-[var(--border-subtle)] cursor-pointer"
                                                >
                                                    <ObliqueIcon name="arrow_back" size={15} />
                                                    <span>Back to Preferences</span>
                                                </button>

                                                <span className="text-[12px] font-semibold text-[var(--text-primary)]">
                                                    Sound & Tone Studio
                                                </span>

                                                <button
                                                    type="button"
                                                    onClick={() => setIsOpen(false)}
                                                    className="w-8 h-8 rounded-lg flex items-center justify-center bg-transparent border-none cursor-pointer text-[var(--text-tertiary)] hover:bg-[var(--bg-muted)] hover:text-[var(--text-primary)] transition-colors p-0"
                                                >
                                                    <ObliqueIcon name="close" size={20} />
                                                </button>
                                            </div>

                                            {/* Studio Body */}
                                            <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4">
                                                {/* Acoustic Hero Card with Micro-Animated notification_sound Icon */}
                                                <div className="p-4 rounded-xl bg-[var(--bg-subtle)] border border-[var(--border-subtle)] space-y-3">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-10 h-10 rounded-xl bg-[var(--accent-subtle)] text-[var(--accent)] flex items-center justify-center shrink-0">
                                                                <NotificationSoundVibrationIcon isSoundOn={soundEnabled} size={22} color="var(--accent)" />
                                                            </div>
                                                            <div>
                                                                <div className="flex items-center gap-1.5">
                                                                    <span className="text-xs font-semibold text-[var(--text-primary)]">
                                                                        {availableTones.find(t => t.id === selectedTone)?.name || 'Apple Glass'}
                                                                    </span>
                                                                    <span className="text-[9.5px] px-1.5 py-0.2 rounded-full font-medium bg-sky-500/10 text-sky-600 dark:text-sky-400">
                                                                        {availableTones.find(t => t.id === selectedTone)?.badge || 'Chime'}
                                                                    </span>
                                                                </div>
                                                                <div className="text-[11px] text-[var(--text-secondary)] mt-0.5">
                                                                    Active acoustic alert profile
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Play Test Button */}
                                                        <button
                                                            type="button"
                                                            onClick={() => previewTone(selectedTone)}
                                                            className="px-3.5 py-1.5 rounded-lg bg-[var(--accent)] hover:bg-[var(--accent)]/90 text-white text-xs font-medium cursor-pointer border-none flex items-center gap-1.5 shadow-xs transition-transform active:scale-95"
                                                        >
                                                            <ObliqueIcon name="volume_up" size={14} color="white" />
                                                            <span>Play Test</span>
                                                        </button>
                                                    </div>

                                                    {/* Waveform Equalizer */}
                                                    <div className="pt-2 border-t border-[var(--border-subtle)] flex items-center justify-between">
                                                        <span className="text-[10.5px] text-[var(--text-tertiary)] flex items-center gap-1">
                                                            <ObliqueIcon name="graphic_eq" size={13} />
                                                            <span>Synthesized Waveform</span>
                                                        </span>
                                                        <div className="flex items-end gap-1 h-4">
                                                            {[6, 14, 9, 18, 12, 16, 7, 13, 8].map((h, i) => (
                                                                <motion.span
                                                                    key={i}
                                                                    className="w-1 bg-[var(--accent)] rounded-full"
                                                                    animate={soundEnabled ? {
                                                                        height: [`${h * 0.3}px`, `${h}px`, `${h * 0.25}px`, `${h * 0.85}px`, `${h * 0.3}px`]
                                                                    } : { height: '2.5px' }}
                                                                    transition={{
                                                                        duration: 1.2,
                                                                        repeat: Infinity,
                                                                        delay: i * 0.1,
                                                                        ease: 'easeInOut'
                                                                    }}
                                                                />
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Master Sound Switch */}
                                                <div className="p-4 rounded-xl bg-[var(--bg-subtle)] border border-[var(--border-subtle)] flex items-center justify-between">
                                                    <div>
                                                        <div className="text-xs font-semibold text-[var(--text-primary)]">
                                                            Notification Sounds
                                                        </div>
                                                        <div className="text-[11px] text-[var(--text-secondary)]">
                                                            Acoustic chimes and call ringtones
                                                        </div>
                                                    </div>

                                                    <button 
                                                        type="button"
                                                        onClick={() => setSoundEnabled(!soundEnabled)}
                                                        className={`w-9 h-5 rounded-full relative cursor-pointer transition-colors border-none p-0 shrink-0 ${
                                                            soundEnabled ? 'bg-[var(--accent)]' : 'bg-[var(--bg-muted)]'
                                                        }`}
                                                    >
                                                        <div className={`w-3.5 h-3.5 rounded-full bg-white absolute top-0.5 transition-all shadow-xs ${
                                                            soundEnabled ? 'left-[18px]' : 'left-[3px]'
                                                        }`} />
                                                    </button>
                                                </div>

                                                {/* Library Filter */}
                                                <div className="flex items-center justify-between px-1 pt-2">
                                                    <span className="text-[11px] font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
                                                        Acoustic Library
                                                    </span>
                                                    <div className="flex p-0.5 rounded-lg bg-[var(--bg-muted)] border border-[var(--border-subtle)] text-[11px]">
                                                        {[
                                                            { id: 'all', label: `All (${availableTones.length})` },
                                                            { id: 'chime', label: 'Chimes' },
                                                            { id: 'ringtone', label: 'Ringtones' }
                                                        ].map(tab => (
                                                            <button
                                                                key={tab.id}
                                                                type="button"
                                                                onClick={() => setToneFilter(tab.id)}
                                                                className={`px-2.5 py-0.5 rounded-md border-none cursor-pointer text-[11px] font-medium transition-all ${
                                                                    toneFilter === tab.id
                                                                        ? 'bg-[var(--bg-surface)] text-[var(--text-primary)] shadow-2xs font-semibold'
                                                                        : 'bg-transparent text-[var(--text-tertiary)] hover:text-[var(--text-primary)]'
                                                                }`}
                                                            >
                                                                {tab.label}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Tones List */}
                                                <div className="space-y-1.5 max-h-[330px] overflow-y-auto custom-scrollbar pr-1">
                                                    {availableTones
                                                        .filter(t => toneFilter === 'all' || t.type === toneFilter)
                                                        .map(tone => {
                                                            const isSelected = selectedTone === tone.id;
                                                            return (
                                                                <div
                                                                    key={tone.id}
                                                                    onClick={() => setSelectedTone(tone.id)}
                                                                    className={`px-3 py-2.5 rounded-xl flex items-center justify-between cursor-pointer transition-all border ${
                                                                        isSelected
                                                                            ? 'bg-[var(--accent-subtle)]/40 border-[var(--accent)]/40 text-[var(--text-primary)] shadow-2xs'
                                                                            : 'bg-[var(--bg-subtle)] hover:bg-[var(--bg-muted)] border-[var(--border-subtle)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                                                                    }`}
                                                                >
                                                                    <div className="flex items-center gap-3 min-w-0">
                                                                        <div className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                                                                            isSelected
                                                                                ? 'border-[var(--accent)] bg-[var(--accent)] text-white'
                                                                                : 'border-[var(--border)] bg-[var(--bg-surface)]'
                                                                        }`}>
                                                                            {isSelected && <ObliqueIcon name="check" size={10} color="white" />}
                                                                        </div>
                                                                        <div className="min-w-0">
                                                                            <span className="text-xs font-medium text-[var(--text-primary)] truncate">
                                                                                {tone.name}
                                                                            </span>
                                                                        </div>
                                                                    </div>

                                                                    <button
                                                                        type="button"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            previewTone(tone.id);
                                                                        }}
                                                                        className="w-7 h-7 rounded-lg flex items-center justify-center bg-[var(--bg-surface)] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)] hover:text-[var(--accent)] border border-[var(--border-subtle)] cursor-pointer transition-all shrink-0 ml-2"
                                                                        title={`Preview ${tone.name}`}
                                                                    >
                                                                        <ObliqueIcon name="volume_up" size={13} />
                                                                    </button>
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </motion.div>
                            </>
                        )}
                    </AnimatePresence>
                </div>,
                document.body
            )}
        </div>
    );
}
